<template>
  <Flex direction="column">
    <Flex
      :direction="$screen === 's' ? 'column' : 'row'"
      equalWidth
    >
      <BaseInput
        v-model="zipcode"
        :required="required"
        :label="$t('global.postal_code')"
        v-test="`${testId}-zipcode`"
      />
      <Flex
        align="end"
        fullWidth
      >
        <Flex
          direction="column"
          grow
        >
          <BaseInput
            v-model="houseNumber"
            :label="$t('global.house_number')"
            v-test="`${testId}-houseNumber`"
          />
        </Flex>
        <BaseButton
          icon="search"
          color="inverted"
          :disabled="!zipcode || !houseNumber"
          :loading="addressLookupLoading"
          :title="$t('global.actions.find_address')"
          @click="lookupAddress"
          v-test="`${testId}-address-lookup`"
        />
      </Flex>
    </Flex>
    <BaseAlert
      v-if="addressNotFound"
      color="warning"
      :text="$t('global.flash.address_not_found')"
      v-test="`${testId}-address-not-found`"
    />
    <Flex
      :direction="$screen === 's' ? 'column' : 'row'"
      equalWidth
    >
      <BaseInput
        v-model="street"
        :required="required"
        :label="$t('global.address')"
        v-test="`${testId}-address`"
      />
      <BaseInput
        v-model="city"
        :required="required"
        :label="$t('global.city')"
        v-test="`${testId}-city`"
      />
    </Flex>
  </Flex>
</template>

<script
  lang="ts"
  setup
>
import { useLazyQuery } from '@vue/apollo-composable';
import { computed, ref, watch } from 'vue';
import { combineAddress, splitAddress } from './address-parsing';
import { ADDRESS_LOOKUP } from './graphql';

const props = withDefaults(
  defineProps<{
    address?: string;
    // When showHouseNumber is set, the house number gets its own field next to the address
    // instead of the default of combining it into the address string - `address` then holds
    // only the street, and houseNumber is read/emitted as its own field.
    houseNumber?: string;
    showHouseNumber?: boolean;
    zipcode?: string;
    city?: string;
    required?: boolean;
    testId?: string;
  }>(),
  {
    address: '',
    houseNumber: '',
    showHouseNumber: false,
    zipcode: '',
    city: '',
    required: false,
    testId: 'customer',
  },
);

const emit = defineEmits<{
  (e: 'update:address', value: string): void;
  (e: 'update:houseNumber', value: string): void;
  (e: 'update:zipcode', value: string): void;
  (e: 'update:city', value: string): void;
  (e: 'update:notFound', value: boolean): void;
}>();

const street = ref('');
const houseNumber = ref('');
const numberFirst = ref(false);
const addressNotFound = ref(false);

const applySplit = (value: string) => {
  const result = splitAddress(value, numberFirst.value);
  street.value = result.street;
  houseNumber.value = result.houseNumber;
  numberFirst.value = result.numberFirst;
};

if (props.showHouseNumber) {
  street.value = props.address;
  houseNumber.value = props.houseNumber;
} else {
  applySplit(props.address);
}

watch(
  () => props.address,
  (value) => {
    if (props.showHouseNumber) {
      if (value !== street.value) {
        street.value = value;
      }
      return;
    }

    if (
      value !==
      combineAddress(street.value, houseNumber.value, numberFirst.value)
    ) {
      applySplit(value);
    }
  },
);

if (props.showHouseNumber) {
  watch(
    () => props.houseNumber,
    (value) => {
      if (value !== houseNumber.value) {
        houseNumber.value = value;
      }
    },
  );
}

// numberFirst is watched too since a re-lookup can flip it without street/houseNumber changing.
watch(
  [street, houseNumber, numberFirst],
  ([newStreet, newHouseNumber, isNumberFirst]) => {
    if (props.showHouseNumber) {
      emit('update:address', newStreet);
      emit('update:houseNumber', newHouseNumber);
      return;
    }

    emit(
      'update:address',
      combineAddress(newStreet, newHouseNumber, isNumberFirst),
    );
  },
);

const zipcode = computed({
  get: () => props.zipcode,
  set: (value) => emit('update:zipcode', value),
});

const city = computed({
  get: () => props.city,
  set: (value) => emit('update:city', value),
});

// Manual button looks up the exact address for a typed-in zipcode + house number.
const {
  load: loadAddress,
  refetch: refetchAddress,
  loading: addressLookupLoading,
  onResult: onAddressLookupResult,
} = useLazyQuery(ADDRESS_LOOKUP);

onAddressLookupResult(({ data }) => {
  if (!data?.addressLookup) {
    return;
  }

  const {
    street: foundStreet,
    city: foundCity,
    numberFirst: foundNumberFirst,
  } = data.addressLookup;

  if (!foundStreet && !foundCity) {
    addressNotFound.value = true;

    return;
  }

  addressNotFound.value = false;
  numberFirst.value = !!foundNumberFirst;

  if (foundStreet) {
    street.value = foundStreet;
  }

  if (foundCity) {
    city.value = foundCity;
  }
});

const lookupAddress = () => {
  if (!zipcode.value || !houseNumber.value) {
    return;
  }

  addressNotFound.value = false;

  const variables = { zipcode: zipcode.value, houseNumber: houseNumber.value };

  loadAddress(undefined, variables) || refetchAddress(variables);
};
</script>
